import React, { useState } from 'react';
import { Heart, MapPin, Bed, Bath, Maximize } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface PropertyCardProps {
  id: string;
  title: string;
  location: string;
  price: number;
  imageUrl: string;
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  type?: string;
  price_unit?: string;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
}

// ── Shared helpers ────────────────────────────────────────────────────────────

const LETTER_PALETTES: Record<string, [string, string]> = {
  A: ['#1a1a2e', '#e94560'], B: ['#16213e', '#0f3460'], C: ['#0f3460', '#533483'],
  D: ['#533483', '#e94560'], E: ['#2b2d42', '#ef233c'], F: ['#1b4332', '#40916c'],
  G: ['#1d3557', '#457b9d'], H: ['#3d405b', '#81b29a'], I: ['#264653', '#2a9d8f'],
  J: ['#3d2b1f', '#f4a261'], K: ['#023e8a', '#0077b6'], L: ['#370617', '#e85d04'],
  M: ['#0c0c0c', '#ffc800'], N: ['#1b263b', '#415a77'], O: ['#3a0ca3', '#7209b7'],
  P: ['#480ca8', '#b5179e'], Q: ['#560bad', '#f72585'], R: ['#10002b', '#e0aaff'],
  S: ['#03045e', '#00b4d8'], T: ['#004b23', '#70e000'], U: ['#370617', '#f48c06'],
  V: ['#0c0c0c', '#ffc800'], W: ['#0d1b2a', '#1b98e0'], X: ['#2d00f7', '#8900f2'],
  Y: ['#1a1a1a', '#f9c74f'], Z: ['#212121', '#ff6b6b'],
};

/** Small red dot + label shown when a field value is missing */
const Missing: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex items-center gap-1 text-[10px] text-light-subtle dark:text-dark-subtle">
    {label}
    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" title="Missing" />
  </span>
);

// ── Letter thumbnail ──────────────────────────────────────────────────────────

const LetterThumbnail: React.FC<{ title: string; type?: string }> = ({ title, type }) => {
  const letter = (title?.[0] || 'P').toUpperCase();
  const [bg, accent] = LETTER_PALETTES[letter] || ['#0c0c0c', '#ffc800'];
  return (
    <div
      className="w-full h-full flex items-center justify-center"
      style={{ background: `linear-gradient(135deg, ${bg} 0%, ${accent}28 100%)` }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-5 -right-5 w-20 h-20 rounded-full opacity-[0.12]" style={{ background: accent }} />
        <div className="absolute -bottom-4 -left-4 w-14 h-14 rounded-full opacity-[0.12]" style={{ background: accent }} />
      </div>
      <div
        className="relative flex items-center justify-center w-12 h-12 rounded-full shadow-md"
        style={{ background: `${accent}1a`, border: `1.5px solid ${accent}50` }}
      >
        <span className="text-xl font-black leading-none" style={{ color: accent }}>{letter}</span>
      </div>
      {type && (
        <span className="absolute bottom-8 left-2.5 text-[9px] font-bold uppercase tracking-widest opacity-50" style={{ color: accent }}>
          {type}
        </span>
      )}
    </div>
  );
};

// ── Card ──────────────────────────────────────────────────────────────────────

const PropertyCard: React.FC<PropertyCardProps> = ({
  id, title, location, price, imageUrl,
  bedrooms, bathrooms, area, type,
  price_unit = 'month',
  isFavorite = false, onToggleFavorite,
}) => {
  const [imgError, setImgError] = useState(false);
  const showThumbnail = !imageUrl || imgError;

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className="group relative rounded-xl overflow-hidden
        bg-light-card dark:bg-dark-card
        border border-light-border dark:border-dark-border
        shadow-soft hover:shadow-medium transition-shadow"
    >
      <Link to={`/properties/${id}`} className="block">

        {/* Image */}
        <div className="relative h-36 overflow-hidden">
          {showThumbnail
            ? <LetterThumbnail title={title} type={type} />
            : <img src={imageUrl} alt={title} onError={() => setImgError(true)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          }
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" />

          {onToggleFavorite && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(id); }}
              className="absolute top-2.5 right-2.5 p-1.5 rounded-full
                bg-light-bg/80 dark:bg-dark-bg/80 backdrop-blur-sm
                hover:bg-light-bg dark:hover:bg-dark-card transition-colors"
            >
              <Heart size={15} className={isFavorite ? 'fill-accent text-accent' : 'text-light-subtle dark:text-dark-subtle'} />
            </button>
          )}

          {/* Type badge — bottom left */}
          <div className="absolute bottom-2.5 left-2.5 px-2 py-0.5 rounded-full
            bg-light-bg/85 dark:bg-dark-bg/85 backdrop-blur-sm
            text-[10px] font-bold uppercase tracking-wider
            text-light-text dark:text-dark-text capitalize">
            {type || <span className="flex items-center gap-1">Type <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" /></span>}
          </div>
        </div>

        {/* Info */}
        <div className="p-3">

          {/* Title */}
          {title
            ? <h3 className="text-sm font-semibold text-light-text dark:text-dark-text line-clamp-1">{title}</h3>
            : <Missing label="Title" />
          }

          {/* Location */}
          <p className="text-[11px] text-light-subtle dark:text-dark-subtle flex items-center gap-1 mt-0.5">
            <MapPin size={10} className="text-primary dark:text-golden flex-shrink-0" />
            {location
              ? <span className="truncate">{location}</span>
              : <Missing label="Location" />
            }
          </p>

          {/* Specs */}
          <div className="flex items-center gap-3 mt-2 text-[11px] text-light-subtle dark:text-dark-subtle">
            {bedrooms != null
              ? <span className="flex items-center gap-0.5"><Bed size={10} /> {bedrooms}</span>
              : <Missing label="Beds" />
            }
            {bathrooms != null
              ? <span className="flex items-center gap-0.5"><Bath size={10} /> {bathrooms}</span>
              : <Missing label="Baths" />
            }
            {area != null
              ? <span className="flex items-center gap-0.5"><Maximize size={10} /> {area}m²</span>
              : <Missing label="Area" />
            }
          </div>

          {/* Price */}
          <div className="mt-2">
            {price
              ? <>
                  <span className="text-sm font-bold text-primary dark:text-golden">KSh {price.toLocaleString()}</span>
                  <span className="text-[10px] text-light-subtle dark:text-dark-subtle ml-0.5">/{price_unit}</span>
                </>
              : <Missing label="Price" />
            }
          </div>

        </div>
      </Link>
    </motion.div>
  );
};

export default PropertyCard;
